import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useTx } from '../../hooks/useTx';
import { usd, toUSDT, shortAddr } from '../../utils/format';
import AdminAction from './AdminAction';
import PoolCard from './PoolCard';
import UserLookup from './UserLookup';
import Blacklist from './Blacklist';

const POOLS = [
  { poolType: 1, name: 'Silver Pool', mark: 'S', cls: 'pool-silver', distributeMethod: 'setSilverPoolIncome', resetMethod: 'resetSilverAccumulation' },
  { poolType: 2, name: 'Gold Pool', mark: 'G', cls: 'pool-gold', distributeMethod: 'setGoldPoolIncome', resetMethod: 'resetGoldAccumulation' },
  { poolType: 3, name: 'Diamond Pool', mark: 'D', cls: 'pool-diamond', distributeMethod: 'setDiamondPoolIncome', resetMethod: 'resetDiamondAccumulation' },
  { poolType: 4, name: 'Special Bonus', mark: '★', cls: 'pool-special', distributeMethod: 'setSpecialPoolIncome', resetMethod: 'resetSpecialAccumulation' },
];

// Validators -------------------------------------------------
const reqAmount = (v) => {
  if (!v || isNaN(Number(v)) || Number(v) <= 0) throw 'Enter a valid amount.';
  return toUSDT(v);
};
const reqInt = (v) => {
  if (!v || !/^\d+$/.test(String(v))) throw 'Enter a valid whole number.';
  return v;
};
const reqAddr = (v) => {
  if (!v || !ethers.utils.isAddress(v.trim())) throw 'Enter a valid wallet address.';
  return ethers.utils.getAddress(v.trim());
};

export default function AdminPanel({ role }) {
  const { isOwner } = role;
  const { evittaRead } = useContracts();
  const [ov, setOv] = useState(null);
  const [bump, setBump] = useState(0);
  const [section, setSection] = useState('pools');
  const refresh = () => setBump((b) => b + 1);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Live overview reads
  useEffect(() => {
    if (!evittaRead) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [reserve, router, dedu, accs, counts, joinOn, missOn, total] = await Promise.all([
          evittaRead.reservePool().catch(() => 0),
          evittaRead.routerPool().catch(() => 0),
          evittaRead.withdrawalDeductionPercent().catch(() => 0),
          evittaRead.getPoolAccumulations().catch(() => null),
          evittaRead.getPoolEligibleCounts().catch(() => null),
          evittaRead.routerJoinEnabled().catch(() => null),
          evittaRead.routerMissingEnabled().catch(() => null),
          evittaRead.totalUsers().catch(() => 0),
        ]);
        if (cancelled || !mounted.current) return;
        setOv({ reserve, router, dedu: Number(dedu), accs, counts, joinOn, missOn, total: Number(total) });
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [evittaRead, bump]);

  return (
    <div className="tab fade-in">
      {/* Role banner */}
      <div className="card glow" style={{ marginTop: 14 }}>
        <div className="card-row">
          <div>
            <div className="gold-text" style={{ fontWeight: 800, fontSize: 18 }}>Control Center</div>
            <div className="dim" style={{ fontSize: 12, marginTop: 2 }}>
              Signed in as {isOwner ? 'Owner' : 'Admin'}
            </div>
          </div>
          <span className={`pill ${isOwner ? 'gold' : 'blue'}`}>{isOwner ? 'OWNER' : 'ADMIN'}</span>
        </div>
      </div>

      {/* Overview (always visible) */}
      <div className="section-title"><span className="bar" />Overview</div>
      <div className="grid-2">
        <Mini label="Total Users" value={ov ? ov.total : '—'} />
        <Mini label="Reserve Pool" value={ov ? usd(ov.reserve) : '—'} gold />
        <Mini label="Withdraw Fee" value={ov ? `${ov.dedu}%` : '—'} />
        {/* Router pool — OWNER ONLY */}
        {isOwner && <Mini label="Router Pool" value={ov ? usd(ov.router) : '—'} gold />}
      </div>
      <p className="dim" style={{ fontSize: 11, margin: '8px 4px 0', lineHeight: 1.4 }}>
        Reserve pool collects all <b>missing income</b> — sponsor & level income that can't be paid
        (upline lacks the package, or a ceiling is hit).
      </p>

      {/* Section menu */}
      <div className="seg">
        <button className={section === 'pools' ? 'on' : ''} onClick={() => setSection('pools')}>Pools</button>
        <button className={section === 'users' ? 'on' : ''} onClick={() => setSection('users')}>Users</button>
        <button className={section === 'settings' ? 'on' : ''} onClick={() => setSection('settings')}>Settings</button>
      </div>

      {/* ============ POOLS ============ */}
      {section === 'pools' && (
        <div className="fade-in">
          {POOLS.map((p, i) => (
            <PoolCard
              key={p.poolType}
              poolType={p.poolType}
              name={p.name}
              mark={p.mark}
              cls={p.cls}
              accumulated={ov && ov.accs ? ov.accs[i] : 0}
              eligible={ov && ov.counts ? Number(ov.counts[i]) : 0}
              distributeMethod={p.distributeMethod}
              resetMethod={p.resetMethod}
              onDone={refresh}
              reloadKey={bump}
            />
          ))}
          <div className="card">
            <ResetBtn method="resetAllPoolAccumulations" label="Reset ALL Pool Accumulations" onDone={refresh} />
          </div>
        </div>
      )}

      {/* ============ USERS ============ */}
      {section === 'users' && (
        <div className="fade-in">
          <UserLookup />

          <Blacklist />

          <div className="section-title"><span className="bar" />Virtual Users</div>
          <AdminAction
            title="Virtual Register"
            desc="Create a virtual user under a referrer."
            fields={[
              { key: 'addr', label: 'User address', placeholder: '0x…' },
              { key: 'ref', label: 'Referrer ID', placeholder: '100000', numeric: true },
            ]}
            method="virtualRegister"
            build={(v) => [reqAddr(v.addr), reqInt(v.ref)]}
            btnLabel="Create Virtual User"
            successMsg="Virtual user created."
            onDone={refresh}
          />
          <AdminAction
            title="Virtual Upgrade"
            desc="Activate a package for a virtual user."
            fields={[
              { key: 'uid', label: 'User ID', placeholder: '1234', numeric: true },
              { key: 'pkg', label: 'Package (2–9)', placeholder: '2', numeric: true },
            ]}
            method="virtualUpgradePackage"
            build={(v) => [reqInt(v.uid), reqInt(v.pkg)]}
            btnLabel="Virtual Upgrade"
            successMsg="Virtual upgrade done."
          />
          <AdminAction
            title="Virtual Re-Topup"
            desc="Re-topup a package for a virtual user."
            fields={[
              { key: 'uid', label: 'User ID', placeholder: '1234', numeric: true },
              { key: 'pkg', label: 'Package (1–9)', placeholder: '1', numeric: true },
            ]}
            method="virtualRetopupPackage"
            build={(v) => [reqInt(v.uid), reqInt(v.pkg)]}
            btnLabel="Virtual Re-Topup"
            successMsg="Virtual re-topup done."
          />
          <AdminAction
            title="Convert Users to Real"
            desc="Comma-separated user IDs (max 100)."
            fields={[{ key: 'ids', label: 'User IDs', placeholder: '1234, 5678' }]}
            method="convertUsersToReal"
            build={(v) => {
              const ids = String(v.ids || '')
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean);
              if (!ids.length) throw 'Enter at least one ID.';
              if (ids.some((id) => !/^\d+$/.test(id))) throw 'IDs must be numbers.';
              if (ids.length > 100) throw 'Max 100 per batch.';
              return [ids];
            }}
            btnLabel="Convert"
            successMsg="Users converted."
          />
        </div>
      )}

      {/* ============ SETTINGS ============ */}
      {section === 'settings' && (
        <div className="fade-in">
          <div className="section-title"><span className="bar" />Reserve & Fees</div>
          <AdminAction
            title="Claim Reserve Pool"
            desc="Sends the full reserve balance to the admin wallet."
            method="withdraw_"
            build={() => []}
            btnLabel={ov ? `Claim ${usd(ov.reserve)}` : 'Claim Reserve'}
            successMsg="Reserve pool claimed."
            onDone={refresh}
          />
          <AdminAction
            title="Withdrawal Deduction %"
            desc="Fee taken on user withdrawals (0–100)."
            fields={[{ key: 'p', label: 'Percent', placeholder: ov ? String(ov.dedu) : '10', numeric: true }]}
            method="setWithdrawalDeduction"
            build={(v) => {
              const n = Number(v.p);
              if (isNaN(n) || n < 0 || n > 100) throw 'Percent must be 0–100.';
              return [String(Math.floor(n))];
            }}
            btnLabel="Update Fee"
            successMsg="Withdrawal fee updated."
            onDone={refresh}
          />
          <AdminAction
            title="Emergency Withdraw"
            desc="Withdraw USDT from the contract to your wallet."
            fields={[{ key: 'a', label: 'Amount (USDT)', placeholder: '0.0', numeric: true }]}
            method="emergencyWithdraw"
            build={(v) => [reqAmount(v.a)]}
            btnLabel="Withdraw"
            successMsg="Emergency withdrawal sent."
            danger
            onDone={refresh}
          />

          {/* OWNER ONLY: router + ownership */}
          {isOwner && (
            <>
              <div className="section-title"><span className="bar" />Router (Owner)</div>
              <div className="card" style={{ marginBottom: 12 }}>
                <div className="card-row" style={{ marginBottom: 12 }}>
                  <span className="muted" style={{ fontSize: 13 }}>Router Pool Balance</span>
                  <span className="stat-value gold" style={{ fontSize: 18 }}>{ov ? usd(ov.router) : '—'}</span>
                </div>
                <ToggleRow label="Router on Join (0.9%)" on={ov ? ov.joinOn : null} method="setRouterJoinEnabled" onDone={refresh} />
                <ToggleRow label="Router on Missing (1%)" on={ov ? ov.missOn : null} method="setRouterMissingEnabled" onDone={refresh} />
              </div>
              <AdminAction
                title="Withdraw Router Pool"
                desc="Send a custom amount to any address."
                fields={[
                  { key: 'a', label: 'Amount (USDT)', placeholder: '0.0', numeric: true },
                  { key: 'to', label: 'Recipient address', placeholder: '0x…' },
                ]}
                method="claimpool_"
                build={(v) => [reqAmount(v.a), reqAddr(v.to)]}
                btnLabel="Withdraw Router"
                successMsg="Router withdrawal sent."
                onDone={refresh}
              />

              <div className="section-title"><span className="bar" />Ownership (Owner)</div>
              <AdminAction
                title="Update Admin"
                desc="Set a new admin wallet."
                fields={[{ key: 'addr', label: 'New admin address', placeholder: '0x…' }]}
                method="updateAdmin"
                build={(v) => [reqAddr(v.addr)]}
                btnLabel="Update Admin"
                successMsg="Admin updated."
                danger
              />
              <AdminAction
                title="Transfer Ownership"
                desc="Hand over contract ownership. This cannot be undone."
                fields={[{ key: 'addr', label: 'New owner address', placeholder: '0x…' }]}
                method="transferOwnership"
                build={(v) => [reqAddr(v.addr)]}
                btnLabel="Transfer Ownership"
                successMsg="Ownership transferred."
                danger
              />
            </>
          )}
        </div>
      )}

      <div style={{ height: 10 }} />
      <p className="dim" style={{ fontSize: 11, textAlign: 'center' }}>
        {isOwner ? 'Owner' : 'Admin'} · {shortAddr(isOwner ? role.owner : role.admin)}
      </p>
    </div>
  );
}

function Mini({ label, value, gold }) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className={`stat-value ${gold ? 'gold' : ''}`} style={{ fontSize: 19 }}>{value}</div>
    </div>
  );
}

function ResetBtn({ method, label, onDone }) {
  const { run, pending } = useTx();
  return (
    <button
      className="btn ghost sm"
      style={{ width: '100%' }}
      disabled={pending}
      onClick={() => run({ method, args: [], successMsg: `${label} done.`, onDone })}
    >
      {pending ? <span className="spin-inline light" /> : null} {label}
    </button>
  );
}

function ToggleRow({ label, on, method, onDone }) {
  const { run, pending } = useTx();
  const toggle = () =>
    run({ method, args: [!on], successMsg: `${label} ${!on ? 'enabled' : 'disabled'}.`, onDone });
  return (
    <div className="card-row" style={{ padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <span className="muted" style={{ fontSize: 13 }}>{label}</span>
      <button
        className={`pill ${on ? 'ok' : 'off'}`}
        style={{ cursor: 'pointer', opacity: pending ? 0.5 : 1 }}
        onClick={toggle}
        disabled={pending || on == null}
      >
        {on == null ? '…' : on ? 'ON' : 'OFF'}
      </button>
    </div>
  );
}
